import React, { useEffect, useRef, useState } from "react";
import { Layout } from "../components/Layout";

const SECTIONS = [
  { id: "intro", title: "Introduction" },
  { id: "scope", title: "Scope of This Policy" },
  { id: "collect", title: "Information We Collect" },
  { id: "app-permissions", title: "Mobile App Permissions" },
  { id: "use", title: "How We Use Your Information" },
  { id: "legal-basis", title: "Legal Basis for Processing" },
  { id: "share", title: "How We Share Information" },
  { id: "security", title: "Data Security" },
  { id: "retention", title: "Data Retention" },
  { id: "rights", title: "Your Rights & Choices" },
  { id: "deletion", title: "Account & Data Deletion" },
  { id: "children", title: "Children's Privacy" },
  { id: "cookies", title: "Cookies & Tracking" },
  { id: "third-party", title: "Third-Party Services & Links" },
  { id: "transfers", title: "International Data Transfers" },
  { id: "changes", title: "Changes to This Policy" },
  { id: "contact", title: "Contact Us" },
];

const H2 = ({ id, children }) => (
  <h2
    id={id}
    style={{
      fontSize: "1.4rem",
      fontWeight: 800,
      color: "#1a2332",
      marginTop: "2.75rem",
      marginBottom: "1rem",
      scrollMarginTop: "110px",
    }}
  >
    {children}
  </h2>
);

const P = ({ children, style }) => (
  <p style={{ color: "#4b5563", lineHeight: 1.85, fontSize: "1rem", marginBottom: "1rem", ...style }}>
    {children}
  </p>
);

const UL = ({ items }) => (
  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1rem" }}>
    {items.map((item, i) => (
      <li
        key={i}
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
          color: "#4b5563",
          lineHeight: 1.8,
          fontSize: "1rem",
          padding: "0.35rem 0",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#37514B,#00b8b8)",
            marginTop: "0.55rem",
            flexShrink: 0,
          }}
        />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const PrivacyPolicy = () => {
  const [active, setActive] = useState("intro");
  const contentRef = useRef(null);

  useEffect(() => {
    const headings = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!headings.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-120px 0px -70% 0px" }
    );
    headings.forEach((h) => obs.observe(h));
    return () => obs.disconnect();
  }, []);

  return (
    <Layout
      subtitle={"Privacy Policy"}
      title={"Privacy Policy"}
      boxheader={`navbar top-space navbar-expand-lg navbar-light bg-white header-light fixed-top header-reverse-scroll navbar-boxed`}
    >
      <section style={{ padding: "3.5rem 0 6rem", background: "#fff" }}>
        <div className="container">
          {/* Intro banner */}
          <div className="row justify-content-center" style={{ marginBottom: "2.5rem" }}>
            <div className="col-12 col-lg-9">
              <div className="section-tag">Your Privacy Matters</div>
              <div className="accent-line" style={{ margin: "0.75rem 0 1.25rem" }} />
              <h1 className="section-title-modern" style={{ fontSize: "clamp(1.6rem,3.5vw,2.4rem)" }}>
                24Telemed <span>Privacy Policy</span>
              </h1>
              <p style={{ color: "#6b7280", fontSize: "0.95rem", marginBottom: 0 }}>
                Effective Date: August 18, 2026 &nbsp;•&nbsp; Applies to the 24Telemed website and the
                24Telemed mobile app.
              </p>
            </div>
          </div>

          <div className="row justify-content-center g-5">
            {/* Sidebar TOC */}
            <div className="col-12 col-lg-3 d-none d-lg-block">
              <div style={{ position: "sticky", top: 110 }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    color: "#9ca3af",
                    marginBottom: "1rem",
                  }}
                >
                  On this page
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, borderLeft: "2px solid #eef2f2" }}>
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        style={{
                          display: "block",
                          padding: "0.4rem 0 0.4rem 1rem",
                          marginLeft: -2,
                          borderLeft: `2px solid ${active === s.id ? "#00b8b8" : "transparent"}`,
                          color: active === s.id ? "#37514B" : "#6b7280",
                          fontWeight: active === s.id ? 700 : 500,
                          fontSize: "0.9rem",
                          textDecoration: "none",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Content */}
            <div className="col-12 col-lg-8">
              <div ref={contentRef}>
                <H2 id="intro">Introduction</H2>
                <P>
                  24Telemed ("Company", "we", "us", or "our") provides telehealth
                  services — including second medical opinions and 24/7 doctor-on-call consultations — through
                  our website (the "Website") and our 24Telemed mobile application (the "App"), together the
                  "Services". This Privacy Policy explains what information we collect, how we use and share
                  it, and the choices and rights you have. By creating an account or otherwise using the
                  Services, you agree to the collection and use of information as described here.
                </P>
                <P>
                  24Telemed is not intended for medical emergencies. If you are experiencing a medical
                  emergency, please call your local emergency number or go to the nearest hospital immediately.
                </P>

                <H2 id="scope">Scope of This Policy</H2>
                <P>
                  This Policy applies wherever you interact with 24Telemed — browsing the Website, downloading
                  and using the App from the Apple App Store or Google Play, booking or attending a
                  consultation, or contacting our support team. It does not apply to third-party sites or
                  services we merely link to.
                </P>

                <H2 id="collect">Information We Collect</H2>
                <P style={{ marginBottom: "0.5rem", fontWeight: 700, color: "#1a2332" }}>
                  Account &amp; Contact Information
                </P>
                <UL
                  items={[
                    "Full name, email address, phone number, and password when you register.",
                    "Date of birth, gender, and address used to match you with appropriate care.",
                  ]}
                />
                <P style={{ marginBottom: "0.5rem", fontWeight: 700, color: "#1a2332" }}>
                  Health &amp; Medical Information
                </P>
                <UL
                  items={[
                    "Symptoms, medical history, current medications, prior diagnoses, and documents or images you upload for a second opinion or consultation.",
                    "Notes, recommendations, and records generated during a consultation with a physician on our platform.",
                  ]}
                />
                <P style={{ marginBottom: "0.5rem", fontWeight: 700, color: "#1a2332" }}>
                  Device, Usage &amp; Diagnostic Data
                </P>
                <UL
                  items={[
                    "IP address, device model, operating system, unique device identifiers, and mobile network information.",
                    "App usage data such as screens viewed, features used, crash logs, and performance diagnostics, used to keep the App stable and secure.",
                  ]}
                />
                <P style={{ marginBottom: "0.5rem", fontWeight: 700, color: "#1a2332" }}>
                  Payment Information
                </P>
                <P>
                  Payments are processed by PCI-compliant third-party payment processors. We do not store your
                  full card or bank details on our servers — we retain only limited transaction metadata (such
                  as amount, date, and status) needed for billing and support.
                </P>

                <H2 id="app-permissions">Mobile App Permissions</H2>
                <P>
                  The App may request the following device permissions. Each is optional unless required for
                  the specific feature, and you can manage them anytime in your device's Settings:
                </P>
                <UL
                  items={[
                    "Camera & Microphone — required to conduct live video or audio consultations with a doctor.",
                    "Photo Library / Files — to attach medical images, lab results, or documents to your consultation request.",
                    "Push Notifications — to alert you about appointment reminders, messages from your doctor, and service updates.",
                    "Location (optional) — used only if you request the nearest call center or facility; never tracked in the background.",
                  ]}
                />

                <H2 id="use">How We Use Your Information</H2>
                <UL
                  items={[
                    "To create and manage your account and authenticate your identity.",
                    "To connect you with physicians and deliver second-opinion and on-call consultation services.",
                    "To process payments and send receipts, reminders, and appointment notifications.",
                    "To maintain the security, integrity, and performance of the Website and App.",
                    "To respond to support requests and communicate service updates.",
                    "To comply with legal, regulatory, and medical record-keeping obligations.",
                  ]}
                />

                <H2 id="legal-basis">Legal Basis for Processing</H2>
                <P>
                  We process personal and health data on the basis of your consent, the necessity of processing
                  to perform our contract with you (delivering the Services you request), and our legitimate
                  interests in operating a secure and reliable platform. Where required, we process health data
                  in line with the Nigeria Data Protection Act 2023 (NDPA) and applicable Nigeria Data
                  Protection Regulation (NDPR) guidance.
                </P>

                <H2 id="share">How We Share Information</H2>
                <P>We do not sell your personal or health information. We share it only in the following circumstances:</P>
                <UL
                  items={[
                    "With the physicians and specialists you consult, so they can review your case and provide care.",
                    "With service providers who support our operations — hosting, payment processing, customer support, push notification, and analytics providers — under confidentiality obligations.",
                    "When required by law, court order, or to protect the rights, safety, or property of our users or the public.",
                    "In connection with a merger, acquisition, or sale of assets, where your information may transfer as part of that transaction, subject to this Policy.",
                  ]}
                />

                <H2 id="security">Data Security</H2>
                <P>
                  We use industry-standard safeguards — including encryption in transit (TLS/SSL), access
                  controls, and secure hosting — to protect your information. No method of transmission or
                  storage is 100% secure; while we work hard to protect your data, we cannot guarantee absolute
                  security. If we become aware of a breach affecting your personal data, we will notify you and
                  relevant authorities as required by law.
                </P>

                <H2 id="retention">Data Retention</H2>
                <P>
                  We retain account and health information for as long as your account is active and as needed
                  to provide the Services, comply with medical record-keeping and legal obligations, resolve
                  disputes, and enforce our agreements. When no longer needed, we securely delete or anonymize
                  your information.
                </P>

                <H2 id="rights">Your Rights &amp; Choices</H2>
                <UL
                  items={[
                    "Access — request a copy of the personal information we hold about you.",
                    "Correction — ask us to correct inaccurate or incomplete information.",
                    "Deletion — request that we delete your account and associated personal data, subject to legal retention requirements.",
                    "Withdraw consent — opt out of non-essential communications and, where applicable, withdraw consent to processing.",
                    "Notifications — disable push notifications at any time from your device settings.",
                  ]}
                />
                <P>
                  To exercise any of these rights, contact us using the details in the{" "}
                  <a href="#contact" style={{ color: "#00b8b8", fontWeight: 600 }}>
                    Contact Us
                  </a>{" "}
                  section below.
                </P>

                <H2 id="deletion">Account &amp; Data Deletion</H2>
                <P>
                  You can request deletion of your 24Telemed account and personal data at any time, directly
                  from within the App via Profile → Account Settings → Delete Account, or by emailing our
                  support team. We will confirm your request and delete or anonymize your data within a
                  reasonable time, except where we are required to retain certain records (such as medical or
                  billing records) to comply with applicable law.
                </P>

                <H2 id="children">Children's Privacy</H2>
                <P>
                  The Services are intended for users 18 years and older. If you are between 13 and 18, you may
                  only use the Services under the supervision of a parent or legal guardian who agrees to this
                  Policy. We do not knowingly collect personal information from children under 13. If you
                  believe a child has provided us information without appropriate consent, please contact us so
                  we can delete it.
                </P>

                <H2 id="cookies">Cookies &amp; Tracking</H2>
                <P>
                  Our Website uses cookies and similar technologies to keep you signed in, remember
                  preferences, and understand how the Website is used. You can control cookies through your
                  browser settings; disabling them may limit some Website features. The mobile App does not use
                  browser cookies but may use similar device-level identifiers for analytics and crash
                  reporting as described above.
                </P>

                <H2 id="third-party">Third-Party Services &amp; Links</H2>
                <P>
                  The Services may link to or integrate with third-party websites, payment processors, and
                  tools that have their own privacy practices. We are not responsible for the content or
                  privacy practices of third parties. We encourage you to review their privacy policies before
                  providing them information.
                </P>

                <H2 id="transfers">International Data Transfers</H2>
                <P>
                  Because some of our specialists and service providers operate outside Nigeria, your
                  information may be transferred to and processed in other countries. Where this occurs, we
                  take steps to ensure appropriate safeguards are in place to protect your information
                  consistent with this Policy and applicable law.
                </P>

                <H2 id="changes">Changes to This Policy</H2>
                <P>
                  We may update this Privacy Policy from time to time to reflect changes to our practices or
                  for legal, operational, or regulatory reasons. We will post the updated Policy here with a
                  revised effective date, and where changes are material, we will provide additional notice
                  (such as an in-app notification or email).
                </P>

                <H2 id="contact">Contact Us</H2>
                <P>
                  If you have questions about this Privacy Policy or wish to exercise your privacy rights,
                  please contact our Data Protection Officer:
                </P>
                <div
                  style={{
                    background: "#f0f9f9",
                    border: "1px solid rgba(0,184,184,0.15)",
                    borderRadius: 16,
                    padding: "1.5rem 1.75rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <p style={{ margin: "0 0 0.5rem", fontWeight: 700, color: "#1a2332" }}>24Telemed</p>
                  <p style={{ margin: "0 0 0.35rem", color: "#4b5563" }}>
                    <i className="fas fa-map-marker-alt" style={{ color: "#00b8b8", width: 20 }} /> Plot 576
                    Independence Avenue, Central Business District, FCT Abuja, Nigeria
                  </p>
                  <p style={{ margin: "0 0 0.35rem", color: "#4b5563" }}>
                    <i className="fas fa-envelope" style={{ color: "#00b8b8", width: 20 }} />{" "}
                    <a href="mailto:info@24telemed.org" style={{ color: "#4b5563" }}>info@24telemed.org</a>
                  </p>
                  <p style={{ margin: 0, color: "#4b5563" }}>
                    <i className="fas fa-phone-alt" style={{ color: "#00b8b8", width: 20 }} /> 08065209446,
                    08130790883, 09024000024
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
